import { useState, useEffect, useCallback } from 'react';
import { apiFetch, toCurrency, downloadCsv } from '../utils';

const PERIOD_MONTHS = [
  'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December', 'January', 'February', 'March',
];

function buildYearOptions() {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-indexed (0=Jan)
  const currentCalYear = now.getFullYear();
  // UK tax year runs April–March. If before April, current tax year ends this calendar year.
  const latestTaxYearEnd = currentMonth < 3 ? currentCalYear : currentCalYear + 1;
  const options = [];
  for (let endYear = latestTaxYearEnd; endYear >= latestTaxYearEnd - 5; endYear--) {
    options.push({
      label: `${endYear - 1}-${endYear}`,
      value: String(endYear),
    });
  }
  return options;
}

const YEAR_OPTIONS = buildYearOptions();

function SortIcon({ active, direction }) {
  if (!active) return null;
  return (
    <svg
      className={`ml-1 inline h-3 w-3 text-slate-500 transition-transform ${direction === 'desc' ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  );
}

export default function Pensions() {
  const [selectedYear, setSelectedYear] = useState('');
  const [yearLoading, setYearLoading] = useState(false);
  const [yearLoaded, setYearLoaded] = useState(false);
  const [yearError, setYearError] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError] = useState(null);

  const [pensionData, setPensionData] = useState([]);

  const [filterName, setFilterName] = useState('');
  const [sortBy, setSortBy] = useState('jsonOrder');
  const [sortDir, setSortDir] = useState('asc');

  const [pensionUsers, setPensionUsers] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [manageSaving, setManageSaving] = useState(false);
  const [manageError, setManageError] = useState(null);

  const loadPensionUsers = useCallback(async () => {
    setManageLoading(true);
    try {
      const res = await apiFetch('/pensionUsers');
      const users = Array.isArray(res) ? res : Array.isArray(res?.users) ? res.users : [];
      setPensionUsers(users);
    } catch {
      setPensionUsers([]);
    } finally {
      setManageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPensionUsers();
  }, [loadPensionUsers]);

  useEffect(() => {
    if (manageOpen) {
      loadPensionUsers();
    }
  }, [manageOpen, loadPensionUsers]);

  // Step 1: Load payroll data for the selected year
  const handleLoadYear = async () => {
    if (!selectedYear) return;
    setYearLoading(true);
    setYearError(null);
    setYearLoaded(false);
    setSelectedPeriod('');
    setPensionData([]);
    try {
      await apiFetch(`/PensionData/${selectedYear}`);
      setYearLoaded(true);
    } catch (err) {
      setYearError(err.message);
    } finally {
      setYearLoading(false);
    }
  };

  // Step 2: Fetch pension summary for the selected period
  const handleLoadPeriod = async (period) => {
    setSelectedPeriod(period);
    if (!period) {
      setPensionData([]);
      return;
    }
    setPeriodLoading(true);
    setPeriodError(null);
    try {
      const data = await apiFetch(`/pensionSummary/${period}`);
      setPensionData(Array.isArray(data) ? data : []);
    } catch (err) {
      setPeriodError(err.message);
      setPensionData([]);
    } finally {
      setPeriodLoading(false);
    }
  };

  // When year changes, reset downstream state
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setYearLoaded(false);
    setYearError(null);
    setSelectedPeriod('');
    setPensionData([]);
    setPeriodError(null);
  };

  // Sorting
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const normalised = pensionData.map((p) => ({
    name: p?.name ?? '',
    employee: p?.employee != null ? Number(p.employee) : 0,
    employer: p?.employer != null ? Number(p.employer) : 0,
  }));

  const filtered = normalised.filter(
    (r) => !filterName.trim() || r.name.toLowerCase().includes(filterName.trim().toLowerCase())
  );

  const jsonOrderMap = Object.fromEntries(pensionUsers.map((name, idx) => [name, idx]));

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'jsonOrder') {
      const idxA = jsonOrderMap[a.name] ?? 9999;
      const idxB = jsonOrderMap[b.name] ?? 9999;
      const dir = sortDir === 'asc' ? 1 : -1;
      return (idxA - idxB) * dir;
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const va = sortBy === 'name' ? a.name.toLowerCase() : a[sortBy];
    const vb = sortBy === 'name' ? b.name.toLowerCase() : b[sortBy];
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });

  const exportOrder = [...normalised].sort((a, b) => {
    const idxA = jsonOrderMap[a.name] ?? 9999;
    const idxB = jsonOrderMap[b.name] ?? 9999;
    return idxA - idxB;
  });

  // Totals row
  const totals = sorted.reduce(
    (acc, r) => ({ employee: acc.employee + r.employee, employer: acc.employer + r.employer }),
    { employee: 0, employer: 0 }
  );

  // CSV export - always uses JSON file order
  const handleExportCsv = () => {
    const yearLabel = YEAR_OPTIONS.find((o) => o.value === selectedYear)?.label ?? selectedYear;
    const lines = ['Name,Employee Pension,Employer Pension'];
    exportOrder.forEach((r) => {
      lines.push(`"${r.name}",${r.employee.toFixed(2)},${r.employer.toFixed(2)}`);
    });
    const exportTotals = exportOrder.reduce(
      (acc, r) => ({ employee: acc.employee + r.employee, employer: acc.employer + r.employer }),
      { employee: 0, employer: 0 }
    );
    lines.push(`"Total",${exportTotals.employee.toFixed(2)},${exportTotals.employer.toFixed(2)}`);
    downloadCsv(`pension_summary_${yearLabel}_P${selectedPeriod}.csv`, lines.join('\n'));
  };

  const handleSortByJsonOrder = () => {
    setSortBy('jsonOrder');
    setSortDir('asc');
  };

  const handleMoveUser = (idx, direction) => {
    const next = [...pensionUsers];
    const target = idx + direction;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setPensionUsers(next);
  };

  const handleRemoveUser = (idx) => {
    setPensionUsers((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddUser = () => {
    const name = newUserName.trim();
    if (!name || pensionUsers.includes(name)) return;
    setPensionUsers((prev) => [...prev, name]);
    setNewUserName('');
  };

  const handleSavePensionUsers = async () => {
    setManageSaving(true);
    setManageError(null);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/pensionUsers', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token || '',
        },
        body: JSON.stringify({ users: pensionUsers }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.statusText);
      }
      const saved = await res.json();
      setPensionUsers(Array.isArray(saved) ? saved : pensionUsers);
      setManageOpen(false);
      if (selectedPeriod) {
        handleLoadPeriod(selectedPeriod);
      }
    } catch (err) {
      setManageError(err.message);
    } finally {
      setManageSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">Pension Reports</h2>

      {/* Manage Pension Users */}
      <div className="rounded-xl bg-white shadow-md">
        <button
          onClick={() => setManageOpen((o) => !o)}
          className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-slate-50"
        >
          <h3 className="text-sm font-semibold text-slate-900">Manage Pension Users</h3>
          <svg
            className={`h-5 w-5 text-slate-500 transition-transform ${manageOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {manageOpen && (
          <div className="border-t border-slate-100 px-5 pb-5 pt-4">
            <p className="mb-4 text-xs text-slate-500">
              Add, remove, or reorder users. Order determines CSV export order. Click Save to persist.
            </p>
            <div className="mb-4 flex gap-2">
              <input
                type="text"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddUser()}
                placeholder="Full name to add"
                className="h-9 flex-1 rounded-lg bg-slate-50 px-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
              />
              <button
                onClick={handleAddUser}
                disabled={!newUserName.trim()}
                className="h-9 rounded-lg bg-slate-200 px-4 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {manageLoading ? (
              <div className="mb-4 flex items-center gap-2 py-6">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
                <span className="text-sm text-slate-600">Loading users...</span>
              </div>
            ) : (
            <ul className="mb-4 max-h-48 overflow-y-auto rounded-lg border border-slate-200">
              {pensionUsers.length === 0 ? (
                <li className="px-3 py-4 text-center text-sm text-slate-500">No pension users yet. Add one above.</li>
              ) : (
              pensionUsers.map((name, idx) => (
                <li
                  key={`${name}-${idx}`}
                  className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0"
                >
                  <span className="text-sm text-slate-900">{name}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleMoveUser(idx, -1)}
                      disabled={idx === 0}
                      className="flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                      title="Move up"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleMoveUser(idx, 1)}
                      disabled={idx === pensionUsers.length - 1}
                      className="flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                      title="Move down"
                    >
                      <svg className="h-4 w-4 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRemoveUser(idx)}
                      className="flex h-7 w-7 items-center justify-center rounded text-red-500 hover:bg-red-50 hover:text-red-700"
                      title="Remove"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </li>
              ))
              )}
            </ul>
            )}
            {manageError && <p className="mb-3 text-sm text-red-600">{manageError}</p>}
            <button
              onClick={handleSavePensionUsers}
              disabled={manageSaving}
              className="h-9 rounded-lg bg-slate-900 px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {manageSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Year + Period selectors */}
      <div className="rounded-xl bg-white p-5 shadow-md">
        <div className="flex flex-wrap items-end gap-4">
          {/* Financial Year */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="year-select" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Financial Year
            </label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              className="h-10 w-44 rounded-lg bg-slate-50 px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="">Select year...</option>
              {YEAR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <button
            onClick={handleLoadYear}
            disabled={!selectedYear || yearLoading}
            className="h-10 rounded-lg bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {yearLoading ? 'Loading...' : 'Load Year Data'}
          </button>

          {yearLoaded && (
            <>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex flex-col gap-1.5">
                <label htmlFor="period-select" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Period
                </label>
                <select
                  id="period-select"
                  value={selectedPeriod}
                  onChange={(e) => handleLoadPeriod(e.target.value)}
                  className="h-10 w-52 rounded-lg bg-slate-50 px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
                >
                  <option value="">Select period...</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((p) => (
                    <option key={p} value={p}>
                      Period {p} ({PERIOD_MONTHS[p - 1]})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {yearError && <p className="mt-3 text-sm text-red-600">{yearError}</p>}
        {periodError && <p className="mt-3 text-sm text-red-600">{periodError}</p>}

        {yearLoaded && !selectedPeriod && (
          <p className="mt-3 text-sm text-slate-500">
            Year data loaded. Select a period to view the pension summary.
          </p>
        )}
      </div>

      {/* Loading spinner for period data */}
      {periodLoading && (
        <div className="flex items-center gap-3 py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
          <span className="text-sm text-slate-600">Fetching pension summary...</span>
        </div>
      )}

      {/* Data table */}
      {sorted.length > 0 && !periodLoading && (
        <div className="rounded-xl bg-white shadow-md">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 px-5 py-4">
            <span className="mr-auto text-sm font-semibold text-slate-900">
              Pension Summary &mdash;{' '}
              {YEAR_OPTIONS.find((o) => o.value === selectedYear)?.label} P{selectedPeriod}
            </span>

            <button
              onClick={handleSortByJsonOrder}
              className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors ${
                sortBy === 'jsonOrder'
                  ? 'bg-indigo-100 text-indigo-800'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Sort by JSON order
            </button>

            <input
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Filter by name..."
              className="h-9 w-52 rounded-lg bg-slate-50 px-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
            />

            <button
              onClick={handleExportCsv}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th
                    className="cursor-pointer select-none px-5 py-3 transition-colors hover:bg-slate-100"
                    onClick={() => handleSort('name')}
                  >
                    Name <SortIcon active={sortBy === 'name'} direction={sortDir} />
                  </th>
                  <th
                    className="cursor-pointer select-none px-5 py-3 text-right transition-colors hover:bg-slate-100"
                    onClick={() => handleSort('employee')}
                  >
                    Employee Pension <SortIcon active={sortBy === 'employee'} direction={sortDir} />
                  </th>
                  <th
                    className="cursor-pointer select-none px-5 py-3 text-right transition-colors hover:bg-slate-100"
                    onClick={() => handleSort('employer')}
                  >
                    Employer Pension <SortIcon active={sortBy === 'employer'} direction={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((row, i) => (
                  <tr key={i} className="transition-colors hover:bg-slate-50">
                    <td className="whitespace-nowrap px-5 py-3 font-medium text-slate-900">{row.name}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-right text-slate-700">{toCurrency(row.employee)}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-right text-slate-700">{toCurrency(row.employer)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 font-semibold">
                <tr>
                  <td className="px-5 py-3 text-slate-900">Total</td>
                  <td className="px-5 py-3 text-right text-slate-900">{toCurrency(totals.employee)}</td>
                  <td className="px-5 py-3 text-right text-slate-900">{toCurrency(totals.employer)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Empty state after period selected but no data */}
      {!periodLoading && selectedPeriod && sorted.length === 0 && pensionData.length === 0 && (
        <div className="rounded-xl bg-white px-5 py-10 text-center shadow-md">
          <p className="text-sm text-slate-500">No pension data returned for this period.</p>
        </div>
      )}
    </div>
  );
}
